import { describe, useOwnerProps } from '@elliemae/ds-props-helpers';
import React from 'react';
import { useButton } from './config/useButton.js';
import { DSButtonName } from './DSButtonDefinitions.js';
import { BUTTON_TYPES } from '../../constants.js';
import type { DSButtonT } from './react-desc-prop-types.js';
import { DSButtonPropTypes } from './react-desc-prop-types.js';
import type { StyledButtonPropsT } from './styles.js';
import { DefaultButton, FilledButton, OutlineButton, RawButton, StyledPseudoBorder, TextButton } from './styles.js';

type ButtonRendererPropsT = React.PropsWithChildren<StyledButtonPropsT & DSButtonT.StyledButtonInheritedDrilledProps>;
const ButtonRenderer = (unsanitizedProps: ButtonRendererPropsT) => {
  const { children, ...propsToDrill } = unsanitizedProps;
  const buttonType = propsToDrill.$buttonType;
  if (buttonType === BUTTON_TYPES.FILLED || buttonType === BUTTON_TYPES.ICON_FILLED)
    return <FilledButton {...propsToDrill}>{children}</FilledButton>;
  if (buttonType === BUTTON_TYPES.OUTLINE || buttonType === BUTTON_TYPES.ICON_OUTLINE)
    return <OutlineButton {...propsToDrill}>{children}</OutlineButton>;
  if (buttonType === BUTTON_TYPES.TEXT || buttonType === BUTTON_TYPES.ICON)
    return <TextButton {...propsToDrill}>{children}</TextButton>;
  if (buttonType === BUTTON_TYPES.RAW) return <RawButton {...propsToDrill}>{children}</RawButton>;
  return <DefaultButton {...propsToDrill}>{children}</DefaultButton>;
};

const DSButton: React.ComponentType<DSButtonT.Props> = (props) => {
  const { propsWithDefault, tabIndex, handleOnMouseDown, globalProps, xstyledProps, handleOnKeyDown, handleOnClick } =
    useButton(props);
  const { size, buttonType, innerRef, children, type, isV3, applyAriaDisabled, readOnly, shape } = propsWithDefault;
  const ownerPropsConfig = useOwnerProps(propsWithDefault, { ...props });
  const { shape: removedShapeGlobal, readOnly: removedReadOnlyGlobal, ...propsWithoutReusedGlobals } = globalProps;

  return (
    <ButtonRenderer
      aria-disabled={applyAriaDisabled || readOnly ? true : undefined}
      {...propsWithoutReusedGlobals}
      {...xstyledProps}
      {...ownerPropsConfig}
      $shape={shape}
      $buttonType={buttonType}
      $size={size}
      $isV3={isV3}
      type={type}
      tabIndex={tabIndex}
      innerRef={innerRef}
      onKeyDown={handleOnKeyDown}
      onClick={handleOnClick}
      onMouseDown={handleOnMouseDown}
    >
      <StyledPseudoBorder className="pseudo-border" aria-hidden="true" />
      {children}
    </ButtonRenderer>
  );
};

DSButton.displayName = DSButtonName;
const DSButtonWithSchema = describe(DSButton);
DSButtonWithSchema.propTypes = DSButtonPropTypes;

export { DSButton, DSButtonWithSchema };
